import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Compass, Home, Loader2, LocateFixed, Navigation, AlertTriangle } from "lucide-react";
import { useGeolocation, fmtPrecisao } from "@/hooks/useGeolocation";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

const FONTE_LABEL = { gps: "localização real do aparelho", manual: "marcador ajustado no mapa", endereco: "endereço digitado" };

// Bloco 6 (Iter 99): ponto de partida das rotas — geocodifica o endereço (OSM) e permite
// ajustar arrastando o marcador. Salva direto via POST /empresa/partida (independe do "Salvar" da página).
// Iter 113: botão "Usar minha localização" (GPS do navegador/celular) + geocodificação reversa do endereço.
export function PartidaCard({ empresa, canEdit, onSaved, raio, onRaioChange }) {
  const [endereco, setEndereco] = useState(empresa?.partida_endereco || "");
  const [pos, setPos] = useState(empresa?.partida_lat != null ? { lat: empresa.partida_lat, lng: empresa.partida_lng } : null);
  const [meta, setMeta] = useState({ fonte: empresa?.partida_fonte || null, precisao_m: empresa?.partida_precisao_m ?? null });
  const [busy, setBusy] = useState(false);
  const geo = useGeolocation();
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setEndereco(empresa?.partida_endereco || "");
    setPos(empresa?.partida_lat != null ? { lat: empresa.partida_lat, lng: empresa.partida_lng } : null);
    setMeta({ fonte: empresa?.partida_fonte || null, precisao_m: empresa?.partida_precisao_m ?? null });
  }, [empresa?.partida_endereco, empresa?.partida_lat, empresa?.partida_lng, empresa?.partida_fonte, empresa?.partida_precisao_m]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: false, attributionControl: false }).setView([-15.78, -47.93], 4);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; markerRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!pos) { if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; } return; }
    if (!markerRef.current) {
      const icon = L.divIcon({ className: "", html: '<div style="background:#EA580C;color:#fff;border-radius:9999px;width:30px;height:30px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);font-size:15px">🏠</div>', iconSize: [30, 30], iconAnchor: [15, 15] });
      const m = L.marker([pos.lat, pos.lng], { icon, draggable: canEdit }).addTo(map);
      m.on("dragend", () => { const p = m.getLatLng(); setPos({ lat: p.lat, lng: p.lng }); savePos(p.lat, p.lng); });
      markerRef.current = m;
    } else {
      markerRef.current.setLatLng([pos.lat, pos.lng]);
    }
    map.setView([pos.lat, pos.lng], Math.max(map.getZoom(), 15));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, canEdit]);

  const savePos = async (lat, lng) => {
    try {
      const r = await api.post("/empresa/partida", { endereco, lat, lng, fonte: "manual" });
      setMeta({ fonte: r.data.fonte, precisao_m: null });
      toast.success("Ponto de partida ajustado");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar ponto de partida"); }
  };

  const usarMinhaLocalizacao = async () => {
    let gps;
    try { gps = await geo.locate(); }
    catch (e) { return toast.error(e.message); }
    setBusy(true);
    try {
      const r = await api.post("/empresa/partida", { lat: gps.lat, lng: gps.lng, fonte: "gps", precisao_m: gps.precisao_m, reverse: true });
      setPos({ lat: r.data.lat, lng: r.data.lng });
      setMeta({ fonte: "gps", precisao_m: gps.precisao_m });
      if (r.data.endereco) setEndereco(r.data.endereco);
      const prec = fmtPrecisao(gps.precisao_m);
      if (gps.precisao_m > 100) toast.warning(`Localização salva com precisão baixa (${prec}). Arraste o marcador até a porta da loja ou tente pelo celular.`);
      else toast.success(`Ponto de partida definido pela sua localização (${prec})${r.data.endereco ? ` — ${r.data.endereco}` : ""}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar ponto de partida"); }
    finally { setBusy(false); }
  };

  const localizar = async () => {
    if (!endereco.trim()) return toast.error("Digite o endereço de partida");
    setBusy(true);
    try {
      const r = await api.post("/empresa/partida", { endereco });
      setPos({ lat: r.data.lat, lng: r.data.lng });
      setMeta({ fonte: "endereco", precisao_m: null });
      toast.success(`Localizado: ${r.data.display_name || endereco}`);
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Endereço não encontrado"); }
    finally { setBusy(false); }
  };

  const precisaoRuim = meta.fonte === "gps" && meta.precisao_m != null && meta.precisao_m > 100;

  return (
    <Card data-testid="card-partida">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><Home className="w-4 h-4" />Ponto de Partida das Rotas</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">Endereço de onde os entregadores saem (a farmácia). Usado para otimizar a sequência de entregas e estimar o tempo de chegada no rastreio do cliente. Estando na loja, use <b>“Usar minha localização”</b> para capturar a posição real pelo GPS do aparelho.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label>Endereço de partida</Label>
            <Input value={endereco} onChange={(e) => setEndereco(e.target.value)} disabled={!canEdit} placeholder="Rua, número, bairro" data-testid="input-partida-endereco"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); localizar(); } }} />
          </div>
          {canEdit && (<>
            <Button type="button" onClick={localizar} disabled={busy || geo.busy} className="bg-orange-600 hover:bg-orange-700" data-testid="btn-partida-localizar">
              {busy && !geo.busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-2" />}Localizar
            </Button>
            <Button type="button" variant="outline" onClick={usarMinhaLocalizacao} disabled={busy || geo.busy}
              className="border-sky-300 text-sky-800 hover:bg-sky-50" data-testid="btn-partida-gps" title="Usa o GPS deste aparelho — ideal estando dentro da loja">
              {geo.busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Navigation className="w-4 h-4 mr-2" />}{geo.busy ? "Obtendo GPS…" : "Usar minha localização"}
            </Button>
          </>)}
        </div>
        {geo.error && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="partida-gps-erro">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{geo.error}
          </div>
        )}
        <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="partida-coords">
          {pos ? <>
            <span>Coordenadas: <span className="mono">{pos.lat.toFixed(5)}, {pos.lng.toFixed(5)}</span></span>
            {meta.fonte && <span className="rounded-full bg-slate-100 px-2 py-0.5" data-testid="partida-fonte" data-fonte={meta.fonte}>
              origem: {FONTE_LABEL[meta.fonte] || meta.fonte}{meta.fonte === "gps" && meta.precisao_m != null ? ` · ${fmtPrecisao(meta.precisao_m)}` : ""}
            </span>}
            {precisaoRuim && <span className="text-amber-700 flex items-center gap-1" data-testid="partida-precisao-aviso"><AlertTriangle className="w-3.5 h-3.5" />precisão baixa — confira o marcador</span>}
            {canEdit && <span>· arraste o marcador para ajustar</span>}
          </> : "Ponto de partida ainda não definido"}
        </div>
        <div ref={containerRef} style={{ height: 220 }} className="rounded-lg border overflow-hidden" data-testid="partida-map" />
        {onRaioChange && (
          <div className="rounded-lg border border-sky-200 bg-sky-50/40 p-3 flex flex-wrap items-end gap-3" data-testid="card-raio-centro">
            <div className="w-40">
              <Label className="flex items-center gap-1.5"><Compass className="w-3.5 h-3.5 text-sky-700" />Raio do Centro (km)</Label>
              <Input type="number" inputMode="decimal" min={0.5} max={20} step={0.5} value={raio ?? ""} disabled={!canEdit} placeholder="2"
                onChange={(e) => onRaioChange(e.target.value)} className="font-mono bg-white" data-testid="input-raio-centro" />
            </div>
            <p className="text-xs text-muted-foreground flex-1 min-w-[220px]">
              Bairros até <b>{raio ? String(raio).replace(".", ",") : "2"} km</b> do centro da cidade são sugeridos como <b>Centro</b>; fora disso, a direção define <b>Zona Norte / Sul / Leste / Oeste</b>.
              Vale para a busca de bairros no OpenStreetMap, o cadastro de bairros e o botão "Sugerir regiões". Salvo com o botão "Salvar alterações". Vazio = 2 km.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
